import { FC, useMemo, useState, useCallback } from "react";
import { Button, DataTable, Column, cn } from "@racoon/ui";
import { IcPlus, IcPencil } from "@racoon/icons";
import { Namespace, useT, type TranslationKey } from "@racoon/i18n";
import { ConnectProto, ContainerGroup } from "@racoon/api";
import { routeGroupsByJobType, zoneNames } from "~/shared/lib/container-group-zone";
import { useTableSettings } from "~/shared/hooks/useTableSettings";
import { useContainerGroupSubmit } from "../hooks/use-container-group-submit";
import { ContainerGroupModal, type ContainerGroupForm } from "./container-group.modal";
import { LocationTableFrame, SaveLocationFirst, TableEmpty } from "./location-table-frame";
import { EMPTY_CELL } from "@racoon/lib";

export const ContainerGroupsBlock: FC<{
  locationId?: number | string;
  organizationId?: number;
  containerGroups?: any[];
  isLoading?: boolean;
  /** Forwarded to the create modal — see its `lockedOrganizationId`. */
  lockedOrganizationId?: string;
  /** Forwarded to the create modal — see its `lockedContractId`. */
  lockedContractId?: string;
}> = ({
  locationId,
  organizationId,
  containerGroups = [],
  isLoading,
  lockedOrganizationId,
  lockedContractId,
}) => {
  const t = useT();

  const { columnConfig, handleColumnConfigChange } = useTableSettings(
    "containers_block",
    organizationId || undefined,
  );

  const [selectedRowId, setSelectedRowId] = useState<bigint | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // State for the container group being edited
  const [editingGroup, setEditingGroup] = useState<ContainerGroup | null>(null);

  const submitContainerGroup = useContainerGroupSubmit();

  // Clear the editing state when creating a new group
  const handleCreate = useCallback(() => {
    setEditingGroup(null);
    setIsCreateModalOpen(true);
  }, []);

  // Set the group to edit and open the modal
  const handleEdit = useCallback((group: ContainerGroup) => {
    setEditingGroup(group);
    setIsCreateModalOpen(true);
  }, []);

  // Unified submit handler: Update if editing, otherwise Create. Failures reach
  // the global toaster; catching them here only to log would swallow that.
  const handleSubmitGroup = useCallback(
    async (formData: ContainerGroupForm) => {
      await submitContainerGroup({
        formData,
        editingGroup,
        locationId: locationId ? BigInt(locationId) : undefined,
      });
    },
    [editingGroup, locationId, submitContainerGroup],
  );

  const columns: Column<ContainerGroup>[] = useMemo(
    () => [
      {
        id: "id",
        header: t(Namespace.PickupLocation, "table.id"),
        accessor: (row: any) => row.id,
        width: 60,
      },
      {
        id: "type",
        header: t(Namespace.PickupLocation, "table.containerType"),
        accessor: (row: any) => row.containerType?.name || row.container_type?.type_name || "—",
      },
      {
        id: "count",
        header: t(Namespace.PickupLocation, "table.count"),
        accessor: (row: any) => row.numberOfContainers,
      },
      {
        id: "volume",
        header: t(Namespace.PickupLocation, "table.totalVolume"),
        accessor: (row: any) => {
          if (row.containerType?.volume && row.numberOfContainers) {
            const total = row.containerType.volume * row.numberOfContainers;
            return `${parseFloat(total.toFixed(1))} m³`;
          }
          return "—";
        },
      },
      {
        id: "customer",
        header: t(Namespace.PickupLocation, "table.customer"),
        accessor: (row: any) => {
          const orgId = row.ownerOrganizationId || row.owner_organization_id;
          const orgName = row.ownerOrganization?.name || row.owner_organization?.name;
          return orgName || (orgId ? `${orgId}` : "—");
        },
      },
      {
        id: "contract",
        header: t(Namespace.PickupLocation, "table.contract"),
        accessor: (row: any) => {
          const contract = row.contract;
          const dealNumber = contract?.dealNumber || contract?.deal_number;
          if (!dealNumber) return "—";
          const name = contract?.name;
          return name ? `№${dealNumber} ${name}` : `№${dealNumber}`;
        },
      },
      {
        id: "zone",
        header: t(Namespace.PickupLocation, "table.zone"),
        accessor: (row: any) => zoneNames(row).join(", ") || EMPTY_CELL,
      },
      {
        id: "route_group",
        header: t(Namespace.PickupLocation, "table.routeGroup"),
        accessor: (row: any) =>
          routeGroupsByJobType(row)
            .map(
              ({ jobType, names }) =>
                `${t(Namespace.Enum, `JOB_TYPE_${ConnectProto.JobType[jobType]}` as TranslationKey)}: ${names.join(", ")}`,
            )
            .join("; ") || EMPTY_CELL,
      },
      {
        id: "actions",
        header: "",
        cell: (info) => {
          const group = info.row.original;
          const isSelected = selectedRowId === group.id;

          if (!isSelected) return null;

          return (
            <button
              type="button"
              className="w-5 h-5 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(group);
              }}
            >
              <IcPencil width={20} height={20} className="text-brand-blue" />
            </button>
          );
        },
        width: 40,
      },
    ],
    [selectedRowId, handleEdit, t],
  );

  return (
    <div className="flex flex-col gap-4 h-full min-w-0 max-w-full">
      <div className="flex justify-center shrink-0">
        <Button
          type="button"
          variant="ghost"
          className="text-brand-blue hover:bg-brand-blue/10 gap-1"
          onClick={handleCreate}
          disabled={!locationId}
        >
          <IcPlus className="w-6 h-6" />
          {t(Namespace.PickupLocation, "createContainerGroup")}
        </Button>
      </div>

      {!locationId && (
        <SaveLocationFirst className="mb-2">
          {t(Namespace.PickupLocation, "table.saveLocationFirst")}
        </SaveLocationFirst>
      )}

      <LocationTableFrame>
        <DataTable
          columns={columns}
          data={containerGroups as any}
          loading={isLoading}
          rowClassName={(group) => {
            const isSelected = selectedRowId === group.id;
            return cn(
              isSelected && "!bg-border-primary cursor-pointer",
              !isSelected && "cursor-pointer",
            );
          }}
          onRowClick={(group) => {
            setSelectedRowId(group.id);
          }}
          empty={<TableEmpty>{t(Namespace.PickupLocation, "table.noContainerGroups")}</TableEmpty>}
          columnConfig={columnConfig}
          onColumnConfigChange={handleColumnConfigChange}
          namespace={Namespace.PickupLocation}
          className="max-h-[25rem] min-w-[900px] border-0"
        />
      </LocationTableFrame>

      <ContainerGroupModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleSubmitGroup}
        locationId={locationId}
        editingGroup={editingGroup} // Pass the group to the modal for editing
        lockedOrganizationId={lockedOrganizationId}
        lockedContractId={lockedContractId}
      />
    </div>
  );
};
